import React, { useState, useMemo, useRef, useEffect, useLayoutEffect } from "react";
import { createPortal } from "react-dom";
import { Check, ChevronDown, Search, X } from "lucide-react";
import { cn } from "../../lib/utils/cn";
import { Label } from "./Input";

export default function SearchableSelect({
  label,
  name,
  value,
  defaultValue,
  onChange,
  onValueChange,
  placeholder,
  options = [],
  getOptionValue = (option) => option.id,
  getOptionLabel = (option) => option.name || option.nom || option.enonce || option.title,
  getOptionSearchText = (option) => getOptionLabel(option).toLowerCase(),
  required = false,
  disabled = false,
  className,
  maxHeight = "200px",
  showClear = true,
  loading = false,
  emptyMessage = "Aucun résultat trouvé"
}) {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedOption, setSelectedOption] = useState(null);
  const [selectedValue, setSelectedValue] = useState(value !== undefined ? value : (defaultValue || ""));
  const [dropdownStyle, setDropdownStyle] = useState({});

  const inputRef = useRef(null);
  const dropdownRef = useRef(null); // The main container
  const dropdownListRef = useRef(null); // The portal content
  const hiddenInputRef = useRef(null);

  // Find selected option based on value
  // Find selected option based on value
  useEffect(() => {
    // If controlled (value provided), use it. Otherwise use internal state.
    // We only use defaultValue for initial state (handled in useState).
    const currentValue = value !== undefined ? value : selectedValue;

    const option = options.find(opt => getOptionValue(opt) === currentValue);
    setSelectedOption(option || null);

    // If controlled, ensure internal state stays in sync
    if (value !== undefined && value !== selectedValue) {
      setSelectedValue(value || "");
      if (hiddenInputRef.current) {
        hiddenInputRef.current.value = value || "";
      }
    }
  }, [value, selectedValue, options, getOptionValue]);

  // Filter options based on search term
  const filteredOptions = useMemo(() => {
    if (!searchTerm.trim()) return options;

    const term = searchTerm.toLowerCase();
    return options.filter(option =>
      getOptionSearchText(option).includes(term)
    );
  }, [options, searchTerm, getOptionSearchText]);

  // Handle option selection
  const handleSelect = (option) => {
    setSelectedOption(option);
    setSearchTerm("");
    setIsOpen(false);

    const optionValue = getOptionValue(option);
    setSelectedValue(optionValue);

    // Update the hidden input field directly
    if (hiddenInputRef.current) {
      hiddenInputRef.current.value = optionValue;
    }

    if (onChange) {
      onChange({ target: { name, value: optionValue } });
    }
    if (onValueChange) {
      onValueChange(optionValue);
    }
  };

  // Handle clear selection
  const handleClear = (e) => {
    e.stopPropagation();
    setSelectedOption(null);
    setSearchTerm("");
    setSelectedValue("");

    // Update the hidden input field directly
    if (hiddenInputRef.current) {
      hiddenInputRef.current.value = "";
    }

    if (onChange) {
      onChange({ target: { name, value: "" } });
    }
    if (onValueChange) {
      onValueChange("");
    }
  };

  // Handle input focus
  const handleFocus = () => {
    if (!disabled) {
      setIsOpen(true);
      setSearchTerm("");
    }
  };

  // Calculate dropdown position
  const updatePosition = () => {
    if (dropdownRef.current && isOpen) {
      const rect = dropdownRef.current.getBoundingClientRect();
      setDropdownStyle({
        top: rect.bottom + window.scrollY + 4, // 4px gap
        left: rect.left + window.scrollX,
        width: rect.width,
      });
    }
  };

  useLayoutEffect(() => {
    if (isOpen) {
      updatePosition();
      // Update position on scroll or resize
      window.addEventListener("scroll", updatePosition, true);
      window.addEventListener("resize", updatePosition);

      return () => {
        window.removeEventListener("scroll", updatePosition, true);
        window.removeEventListener("resize", updatePosition);
      };
    }
  }, [isOpen]);

  // Handle click outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      // Check if click is inside the main component or the portal dropdown
      const isInsideMain = dropdownRef.current && dropdownRef.current.contains(event.target);
      const isInsidePortal = dropdownListRef.current && dropdownListRef.current.contains(event.target);

      if (!isInsideMain && !isInsidePortal) {
        setIsOpen(false);
        setSearchTerm("");
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // Handle keyboard navigation
  const handleKeyDown = (e) => {
    if (e.key === "Escape") {
      setIsOpen(false);
      setSearchTerm("");
    }
  };

  const displayValue = selectedOption ? getOptionLabel(selectedOption) : "";
  const hasValue = selectedOption || (value || defaultValue);

  return (
    <div className="space-y-2 w-full min-w-0 max-w-full">
      {label && <Label htmlFor={name}>{label}</Label>}
      <div className="relative w-full min-w-0 max-w-full" ref={dropdownRef}>
        <div
          className={cn(
            "flex h-10 w-full items-center justify-between rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 min-w-0 max-w-full",
            className
          )}
          onClick={handleFocus}
        >
          <div className="flex items-center flex-1 min-w-0 overflow-hidden">
            {isOpen ? (
              <div className="flex items-center flex-1 min-w-0">
                <Search className="h-4 w-4 mr-2 text-muted-foreground flex-shrink-0" />
                <input
                  ref={inputRef}
                  type="text"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  onKeyDown={handleKeyDown}
                  placeholder={placeholder}
                  className="flex-1 bg-transparent border-none outline-none text-sm min-w-0"
                  autoFocus
                />
              </div>
            ) : (
              <span className={cn(
                "truncate block max-w-full",
                !hasValue && "text-muted-foreground"
              )} title={displayValue}>
                {displayValue || placeholder}
              </span>
            )}
          </div>

          <div className="flex items-center gap-1 ml-2">
            {showClear && hasValue && !disabled && (
              <button
                type="button"
                onClick={handleClear}
                className="p-1 hover:bg-muted rounded-sm"
              >
                <X className="h-3 w-3" />
              </button>
            )}
            <ChevronDown className={cn(
              "h-4 w-4 opacity-50 transition-transform",
              isOpen && "rotate-180"
            )} />
          </div>
        </div>

        {isOpen && createPortal(
          <div
            ref={dropdownListRef}
            className="absolute z-[9999] overflow-auto rounded-md border bg-popover text-popover-foreground shadow-md animate-in fade-in-0 zoom-in-95 pointer-events-auto"
            style={{
              ...dropdownStyle,
              maxHeight: maxHeight,
              "--max-height": maxHeight
            }}
            onMouseDown={(e) => {
              // Prevent click outside from firing when clicking inside the portal
              // This also prevents the parent modal (if any) from closing
              e.stopPropagation();
            }}
            onClick={(e) => {
              e.stopPropagation();
            }}
          >
            {loading ? (
              <div className="flex items-center justify-center py-4">
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-slate-900"></div>
                <span className="ml-2 text-sm text-muted-foreground">Chargement...</span>
              </div>
            ) : filteredOptions.length > 0 ? (
              filteredOptions.map((option) => {
                const optionValue = getOptionValue(option);
                const optionLabel = getOptionLabel(option);
                const isSelected = selectedOption && getOptionValue(selectedOption) === optionValue;

                return (
                  <div
                    key={optionValue}
                    className={cn(
                      "relative flex w-full cursor-pointer select-none items-center rounded-sm py-2 pl-8 pr-2 text-sm outline-none hover:bg-accent hover:text-accent-foreground",
                      isSelected && "bg-accent text-accent-foreground"
                    )}
                    onClick={() => handleSelect(option)}
                  >
                    <span className="absolute left-2 flex h-3.5 w-3.5 items-center justify-center">
                      {isSelected && <Check className="h-4 w-4" />}
                    </span>
                    <span className="truncate" title={optionLabel}>
                      {optionLabel}
                    </span>
                  </div>
                );
              })
            ) : (
              <div className="py-4 text-center text-sm text-muted-foreground">
                {emptyMessage}
              </div>
            )}
          </div>,
          document.body
        )}
      </div>

      {/* Hidden input for form submission */}
      <input
        ref={hiddenInputRef}
        type="hidden"
        name={name}
        value={selectedValue}
        required={required}
      />
    </div>
  );
}
